import React, { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCare } from '../../hooks/useCare'
import { UserRole, getDefaultPermissions } from '../../types/user'
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { auth, db } from '../../config/firebase'
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore'
import { getSubscriptionLimit } from '../../config/subscription'
import ExceededLimitsModal from '../common/ExceededLimitsModal'

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface CreateUserForm {
  name: string
  email: string
  username: string
  password: string
  confirmPassword: string
  role: UserRole
  forcePasswordChange: boolean
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user, userProfile } = useAuth()
  const { currentCase, loadCaseMembers, permissions } = useCare()

  const [form, setForm] = useState<CreateUserForm>({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'caregiver',
    forcePasswordChange: true
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<{
    username: string
    password: string
    email: string
  } | null>(null)

  const [showLimitModal, setShowLimitModal] = useState(false)
  const [limitMessage, setLimitMessage] = useState('')

  // Generar password seguro
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
    const symbols = '!@#$%'
    let password = ''

    // Al menos 1 mayúscula, 1 minúscula, 1 número, 1 símbolo
    password += chars.charAt(Math.floor(Math.random() * 26)) // Mayúscula
    password += chars.charAt(Math.floor(Math.random() * 26) + 26) // Minúscula
    password += chars.charAt(Math.floor(Math.random() * 10) + 52) // Número
    password += symbols.charAt(Math.floor(Math.random() * symbols.length)) // Símbolo

    // Completar hasta 12 caracteres
    for (let i = 4; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    // Mezclar caracteres
    password = password.split('').sort(() => 0.5 - Math.random()).join('')

    setForm(prev => ({
      ...prev,
      password,
      confirmPassword: password
    }))
  }

  // Generar username automático desde el nombre
  const generateUsername = (name: string) => {
    const cleanName = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z\s]/g, '') // Solo letras y espacios
      .trim()

    const parts = cleanName.split(' ').filter(part => part.length > 0)

    if (parts.length === 1) {
      return parts[0]
    } else if (parts.length >= 2) {
      return `${parts[0]}_${parts[parts.length - 1]}`
    }

    return ''
  }

  const handleNameChange = (name: string) => {
    setForm(prev => ({
      ...prev,
      name,
      username: generateUsername(name)
    }))
  }

  const validateForm = (): string | null => {
    if (!form.name.trim()) return 'El nombre es requerido'
    if (!form.email.trim()) return 'El email es requerido'
    if (!form.username.trim()) return 'El username es requerido'
    if (!form.password) return 'La contraseña es requerida'
    if (form.password !== form.confirmPassword) return 'Las contraseñas no coinciden'
    if (form.password.length < 8) return 'La contraseña debe tener al menos 8 caracteres'

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) return 'Email inválido'

    // Validar username
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/
    if (!usernameRegex.test(form.username)) return 'Username debe tener 3-20 caracteres (letras, números, _)'

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    if (!user?.uid || !currentCase) {
      setError('Usuario o caso no válido')
      return
    }

    setLoading(true)
    setError('')

    // Verificar límites
    try {
      const subscriptionPlan = userProfile?.subscriptionPlan || 'free'
      const maxMembers = getSubscriptionLimit(subscriptionPlan, 'maxMembersPerCase')

      const membersQuery = query(
        collection(db, 'case-members'),
        where('caseId', '==', currentCase.id),
        where('isActive', '==', true)
      )
      const membersSnapshot = await getDocs(membersQuery)
      const currentMembersCount = membersSnapshot.size

      if (currentMembersCount >= maxMembers) {
        setLimitMessage(`El plan ${subscriptionPlan} solo permite ${maxMembers} miembros por caso.`)
        setShowLimitModal(true)
        setLoading(false)
        return
      }
    } catch (error) {
      console.error('Error checking limits:', error)
      setError('Error al verificar límites')
      setLoading(false)
      return
    }

    try {
      // 1. Crear usuario en Firebase Auth
      console.log('Creando usuario en Firebase Auth...')
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.password
      )

      const newUser = userCredential.user

      // 2. Actualizar perfil del usuario
      await updateProfile(newUser, {
        displayName: form.username
      })

      // 3. Crear documento de usuario en Firestore
      await setDoc(doc(db, 'users', newUser.uid), {
        email: form.email,
        username: form.username,
        name: form.name,
        createdAt: serverTimestamp(),
        createdBy: user.uid, // Quien creó este usuario
        forcePasswordChange: form.forcePasswordChange,
        settings: {
          theme: 'system',
          language: 'es',
          emailAlerts: true
        }
      })

      // 4. Agregar al caso como miembro
      await setDoc(doc(db, 'case-members', `${currentCase.id}_${newUser.uid}`), {
        userId: newUser.uid,
        caseId: currentCase.id,
        role: form.role,
        permissions: getDefaultPermissions(form.role),
        joinedAt: serverTimestamp(),
        invitedBy: user.uid,
        isActive: true,
        nickname: form.name
      })

      // 5. Mostrar resumen de éxito
      setSuccess({
        username: form.username,
        password: form.password,
        email: form.email
      })

      // 6. Recargar miembros
      await loadCaseMembers(currentCase.id)

      console.log('✅ Usuario creado exitosamente')

    } catch (error: any) {
      console.error('Error creando usuario:', error)

      // Manejar errores específicos de Firebase
      if (error.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado')
      } else if (error.code === 'auth/weak-password') {
        setError('La contraseña es muy débil')
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido')
      } else {
        setError('Error al crear el usuario. Intenta nuevamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      role: 'caregiver',
      forcePasswordChange: true
    })
    setError('')
    setSuccess(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSuccessClose = () => {
    resetForm()
    onSuccess()
    onClose()
  }

  if (!isOpen) return null

  // Pantalla de éxito
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              ¡Usuario Creado Exitosamente!
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Comparte estas credenciales con el usuario:
            </p>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Email:</span>
                  <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded border mt-1">
                    {success.email}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Usuario:</span>
                  <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded border mt-1">
                    {success.username}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Contraseña:</span>
                  <div className="font-mono bg-white dark:bg-gray-800 p-2 rounded border mt-1">
                    {success.password}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  const text = `Credenciales de acceso:\nEmail: ${success.email}\nUsuario: ${success.username}\nContraseña: ${success.password}`
                  navigator.clipboard.writeText(text)
                  alert('Credenciales copiadas al portapapeles')
                }}
                className="w-full btn btn-secondary px-4 py-2"
              >
                📋 Copiar Credenciales
              </button>

              <button
                onClick={handleSuccessClose}
                className="w-full btn btn-primary px-4 py-2"
              >
                ✅ Continuar
              </button>
            </div>

            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-4">
              💡 El usuario deberá cambiar la contraseña en su primer acceso
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            🆕 Crear Usuario Directo
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre completo
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="input w-full"
              placeholder="María García"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="input w-full"
              placeholder="maria@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nombre de usuario
            </label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
              className="input w-full"
              placeholder="maria_garcia"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Solo letras, números y guiones bajos
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Contraseña
              </label>
              <button
                type="button"
                onClick={generatePassword}
                className="text-xs text-primary hover:text-primary/80"
              >
                🎲 Generar
              </button>
            </div>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
              className="input w-full font-mono"
              placeholder="MiPassword123!"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirmar contraseña
            </label>
            <input
              type="text"
              value={form.confirmPassword}
              onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="input w-full font-mono"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Rol en el equipo
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value as UserRole }))}
              className="input w-full"
            >
              <option value="caregiver">Cuidador - Puede gestionar medicamentos libremente</option>
              {permissions.canPromoteToAdmin && (
                <option value="admin">⚡ Administrador - Puede configurar otros usuarios</option>
              )}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {permissions.canPromoteToAdmin
                ? 'Como Administrador Maestro, puedes crear otros administradores'
                : 'Solo el Administrador Maestro puede designar otros administradores'
              }
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="forcePasswordChange"
              checked={form.forcePasswordChange}
              onChange={(e) => setForm(prev => ({ ...prev, forcePasswordChange: e.target.checked }))}
              className="rounded"
            />
            <label htmlFor="forcePasswordChange" className="text-sm text-gray-700 dark:text-gray-300">
              Forzar cambio de contraseña en primer acceso
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary px-4 py-2"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary px-4 py-2"
              disabled={loading}
            >
              {loading ? 'Creando...' : '🚀 Crear Usuario'}
            </button>
          </div>
        </form>
      </div>

      <ExceededLimitsModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Límite de Miembros Alcanzado"
        message={limitMessage}
        limitType="members"
      />
    </div>
  )
}

export default CreateUserModal 